import React, { useMemo, useState } from 'react';
import { translate } from '@utils/i18n';
import { StatCard } from '@ui/molecules/StatCard';
import { KPI_DATA, SUMMARY_DATA } from '../constants';
import useIssuesCaughtVsResolved from '../hooks/useIssuesCaughtVsResolved';
import useTotalOpenIssues from '../hooks/useTotalOpenIssues';
import useAvgProcessingTime from '../hooks/useAvgProcessingTime';
import useAvgResubmissions from '../hooks/useAvgResubmissions';
import useEscapedIssues from '../hooks/useEscapedIssues';
import useTopIssueCategories from '../hooks/useTopIssueCategories';
import useDismissedVsResolved from '../hooks/useDismissedVsResolved';
import useTechsNeedingCoaching from '../hooks/useTechsNeedingCoaching';
import IssuesCaughtChart from './IssuesCaughtChart';
import TopIssueCategoriesChart from './TopIssueCategoriesChart';
import DismissedVsResolvedChart from './DismissedVsResolvedChart';
import TotalOpenIssuesModal from './TotalOpenIssuesModal';
import AvgProcessingTimeModal from './AvgProcessingTimeModal';
import AvgResubmissionsModal from './AvgResubmissionsModal';
import EscapedIssuesModal from './EscapedIssuesModal';
const namespace = 'features.dashboards.inspection_assistant_insights_v2';
function translateTrend(trend) {
    return trend
        .split(' ')
        .map((part) => {
        if (/^[a-z]+\.[a-z_]+$/i.test(part))
            return translate(part, { namespace });
        return part;
    })
        .join(' ');
}
function formatTrend(percent, localeKey) {
    if (percent === 0)
        return `0% ${localeKey}`;
    const sign = percent > 0 ? '+' : '';
    return `${sign}${percent}% ${localeKey}`;
}
function getTrendDirection(percent) {
    if (percent > 0)
        return 'up';
    if (percent < 0)
        return 'down';
    return 'neutral';
}
function getSummaryTrendSentiment(direction, positiveIsGood) {
    if (direction === 'neutral')
        return 'neutral';
    const isGood = positiveIsGood ? direction === 'up' : direction === 'down';
    return isGood ? 'positive' : 'negative';
}
function OverviewTab({ filters }) {
    var _a, _b, _c;
    const [isOpenIssuesModalOpen, setIsOpenIssuesModalOpen] = useState(false);
    const [isAvgTimeModalOpen, setIsAvgTimeModalOpen] = useState(false);
    const [isResubModalOpen, setIsResubModalOpen] = useState(false);
    const [isEscapedModalOpen, setIsEscapedModalOpen] = useState(false);
    const { data, isLoading } = useIssuesCaughtVsResolved(filters);
    const { data: openIssuesData, isLoading: openIssuesLoading } = useTotalOpenIssues(filters);
    const { data: avgTimeData, isLoading: avgTimeLoading } = useAvgProcessingTime(filters);
    const { data: resubData, isLoading: resubLoading } = useAvgResubmissions(filters);
    const { data: escapedData, isLoading: escapedLoading } = useEscapedIssues(filters);
    const { data: topCategoriesData, isLoading: topCategoriesLoading } = useTopIssueCategories(filters);
    const { data: dismissedData, isLoading: dismissedLoading } = useDismissedVsResolved(filters);
    const { data: coachingData } = useTechsNeedingCoaching(filters);
    const kpiCards = useMemo(() => {
        const cards = [...KPI_DATA];
        if (openIssuesData) {
            const t = openIssuesData;
            cards[0] = {
                label: 'kpi.total_existing_issues',
                value: t.totalExistingIssues.toLocaleString(),
                trend: 'kpi.all_time',
                trendDirection: 'neutral',
                trendIsPositive: true
            };
            cards[1] = {
                label: 'kpi.total_open_issues',
                value: t.totalOpenIssues.toLocaleString(),
                trend: formatTrend(t.trendPercent, 'kpi.vs_prior_period'),
                trendDirection: t.trendDirection,
                trendIsPositive: t.trendDirection === 'down' // fewer open = good
            };
        }
        if (avgTimeData) {
            const t = avgTimeData;
            cards[2] = {
                label: 'kpi.avg_processing_time',
                value: t.averageProcessingTime,
                trend: formatTrend(t.trendPercent, 'kpi.vs_prior_period'),
                trendDirection: t.trendDirection,
                trendIsPositive: t.trendDirection === 'down' // faster = good
            };
        }
        if (resubData) {
            const t = resubData;
            cards[3] = {
                label: 'kpi.avg_resubmissions',
                value: String(t.averageResubmissions),
                trend: formatTrend(t.trendPercent, 'kpi.vs_prior_period'),
                trendDirection: t.trendDirection,
                trendIsPositive: t.trendDirection === 'down' // fewer resubs = good
            };
        }
        if (escapedData) {
            const t = escapedData;
            cards[4] = {
                label: 'kpi.escaped_issues',
                value: t.escapedIssues.toLocaleString(),
                trend: formatTrend(t.trendPercent, 'kpi.vs_prior_period'),
                trendDirection: t.trendDirection,
                trendIsPositive: t.trendDirection === 'down' // fewer escaped = good
            };
        }
        return cards;
    }, [openIssuesData, avgTimeData, resubData, escapedData]);
    const summaryCards = useMemo(() => {
        const cards = [...SUMMARY_DATA];
        if (openIssuesData) {
            const issuesDir = getTrendDirection(openIssuesData.totalIssuesTrendPercent);
            cards[0] = {
                label: 'summary.issues_caught',
                value: openIssuesData.totalIssues.toLocaleString(),
                trend: formatTrend(openIssuesData.totalIssuesTrendPercent, 'summary.vs_prior_period'),
                trendDirection: issuesDir === 'neutral' ? undefined : issuesDir,
                trendSentiment: getSummaryTrendSentiment(issuesDir, false)
            };
            const resolutionDir = getTrendDirection(openIssuesData.resolutionRateTrendPercent);
            cards[1] = {
                label: 'summary.resolution_rate',
                value: `${openIssuesData.resolutionRate}%`,
                trend: formatTrend(openIssuesData.resolutionRateTrendPercent, 'summary.vs_prior_period'),
                trendDirection: resolutionDir === 'neutral' ? undefined : resolutionDir,
                trendSentiment: getSummaryTrendSentiment(resolutionDir, true)
            };
            cards[2] = {
                label: 'summary.estimated_time_saved',
                value: openIssuesData.estimatedTimeSaved,
                trend: `~${openIssuesData.estimatedTimePerInspection} summary.per_inspection`,
                trendSentiment: 'default'
            };
        }
        if (coachingData) {
            const coachingDir = getTrendDirection(coachingData.trendPercent);
            cards[3] = {
                label: 'summary.techs_needing_coaching',
                value: String(coachingData.techsNeedingCoaching),
                trend: formatTrend(coachingData.trendPercent, 'summary.vs_prior_period'),
                trendDirection: coachingDir === 'neutral' ? undefined : coachingDir,
                trendSentiment: getSummaryTrendSentiment(coachingDir, false)
            };
        }
        return cards;
    }, [openIssuesData, coachingData]);
    const getKpiOnClick = (index) => {
        if (index === 1)
            return () => setIsOpenIssuesModalOpen(true);
        if (index === 2)
            return () => setIsAvgTimeModalOpen(true);
        if (index === 3)
            return () => setIsResubModalOpen(true);
        if (index === 4)
            return () => setIsEscapedModalOpen(true);
        return undefined;
    };
    return (React.createElement("div", { className: "overview-tab" },
        React.createElement("div", { className: "overview-tab__top" },
            React.createElement(IssuesCaughtChart, { data: (_a = data === null || data === void 0 ? void 0 : data.issuesCaughtVsResolved) !== null && _a !== void 0 ? _a : [], isLoading: isLoading }),
            React.createElement("div", { className: "overview-tab__kpi-stack" }, kpiCards.map((kpi, index) => {
                const onClickHandler = getKpiOnClick(index);
                return (React.createElement(StatCard, { key: kpi.label, variant: onClickHandler ? 'interactive' : 'static', value: kpi.value, label: translate(kpi.label, { namespace }), trend: translateTrend(kpi.trend), trendDirection: kpi.trendDirection, trendSentiment: kpi.trendDirection === 'neutral' ? 'neutral' : kpi.trendIsPositive ? 'positive' : 'negative', isLoading: (index === 0 && openIssuesLoading) ||
                        (index === 1 && openIssuesLoading) ||
                        (index === 2 && avgTimeLoading) ||
                        (index === 3 && resubLoading) ||
                        (index === 4 && escapedLoading), onClick: onClickHandler }));
            }))),
        React.createElement("div", { className: "overview-tab__summary-row" }, summaryCards.map((card) => {
            var _a;
            return (React.createElement(StatCard, { key: card.label, variant: "static", value: card.value, label: translate(card.label, { namespace }), trend: card.trend ? translateTrend(card.trend) : undefined, trendDirection: card.trendDirection, trendSentiment: (_a = card.trendSentiment) !== null && _a !== void 0 ? _a : 'default' }));
        })),
        React.createElement("div", { className: "overview-tab__bottom" },
            React.createElement(TopIssueCategoriesChart, { data: (_b = topCategoriesData === null || topCategoriesData === void 0 ? void 0 : topCategoriesData.topIssueCategories) !== null && _b !== void 0 ? _b : [], isLoading: topCategoriesLoading, className: "overview-tab__bottom-card" }),
            React.createElement(DismissedVsResolvedChart, { data: (_c = dismissedData === null || dismissedData === void 0 ? void 0 : dismissedData.dismissedVsResolved) !== null && _c !== void 0 ? _c : [], isLoading: dismissedLoading, className: "overview-tab__bottom-card" })),
        React.createElement(TotalOpenIssuesModal, { isOpen: isOpenIssuesModalOpen, onClose: () => setIsOpenIssuesModalOpen(false), filters: filters }),
        React.createElement(AvgProcessingTimeModal, { isOpen: isAvgTimeModalOpen, onClose: () => setIsAvgTimeModalOpen(false), filters: filters }),
        React.createElement(AvgResubmissionsModal, { isOpen: isResubModalOpen, onClose: () => setIsResubModalOpen(false), filters: filters }),
        React.createElement(EscapedIssuesModal, { isOpen: isEscapedModalOpen, onClose: () => setIsEscapedModalOpen(false), filters: filters })));
}
export default OverviewTab;
